import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { makeRemoteRemoveLocationUser } from 'main/factories/usecases/remove-location-user';
import { FIVE_SECONDS, showToast, TEN_SECONDS, TOAST_ERROR, TOAST_SUCCESS } from 'lib/utils';
import { RemoveLocationUser } from 'domain/usecases/remove-location-user';

/**
 * Custom hook for removing a location user
 * @param {UseMutationOptions} options - Optional configuration options
 * @returns {Object} Mutation object with various properties and methods
 */
export const useRemoveLocationUser = (
  options?: UseMutationOptions<RemoveLocationUser.Model, Error, RemoveLocationUser.Params>
) => {
  const queryClient = useQueryClient();
  const removeLocationUserService = makeRemoteRemoveLocationUser();

  const removeLocationUserFn = async (params: RemoveLocationUser.Params) => {
    return removeLocationUserService.removeLocationUser(params);
  };

  const { onSuccess: customOnSuccess, onError: customOnError, ...rest } = options || {};

  return useMutation({
    mutationFn: removeLocationUserFn,

    onError: (error, variables, context) => {
      showToast(`Error removing user: ${error.message}`, TOAST_ERROR, TEN_SECONDS);
      customOnError?.(error, variables, context);
    },

    onSuccess: async (data, variables, context) => {
      // Invalidate location users queries
      await queryClient.invalidateQueries({
        queryKey: ['locationUsers', variables.locationId],
      });

      await queryClient.invalidateQueries({
        queryKey: ['locationUsers'],
      });

      showToast('Successfully removed user', TOAST_SUCCESS, FIVE_SECONDS);
      customOnSuccess?.(data, variables, context);
    },

    ...rest,
  });
};
